import { CohortRow } from '../../types/marketing';
import { DASH, formatDay, formatInt, formatPct, platformLabel, SUPPRESSED_TEXT } from './marketingMath';

const DAYS = [['d1', 'D1'], ['d7', 'D7'], ['d30', 'D30']] as const;

/** Shade bucket 0..4 for a returned share; the number in the cell is the message, the shade only helps scanning. */
export function cohortLevel(v: number | null): number {
  if (v == null) return 0;
  return v >= 0.4 ? 4 : v >= 0.25 ? 3 : v >= 0.1 ? 2 : v > 0 ? 1 : 0;
}

const DAY_N = { d1: 1, d7: 7, d30: 30 } as const;

/** True once every user of the cohort week has had `dayN` days: week start + 7 + dayN days is not after `today`. */
export function cohortMatured(cohortWeek: string, dayN: 1 | 7 | 30, today: Date = new Date()): boolean {
  const start = new Date(`${cohortWeek}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return true;
  const due = new Date(start.getTime() + (7 + dayN) * 86_400_000);
  return due.getTime() <= today.getTime();
}

/**
 * A cell's text: the % returned; "<5" only when the cohort is suppressed (fewer than 5 users) AND that day has
 * matured; a day not reached yet is "—" even for a suppressed cohort (Reviewer, #1441).
 */
export function cohortCellText(row: CohortRow, v: number | null, day: keyof typeof DAY_N = 'd30', today?: Date): string {
  if (v != null) return formatPct(v);
  return row.suppressed && cohortMatured(row.cohortWeek, DAY_N[day], today) ? SUPPRESSED_TEXT : DASH;
}

/**
 * Spec 084 — first-open week × D1 / D7 / D30 returned, one grid per platform (GA4, humans only). Cells carry
 * the number; colour is secondary. Cohorts or cells under 5 users show "<5".
 */
export default function CohortGrid({ cohorts, today }: { cohorts: CohortRow[]; today?: Date }) {
  const platforms = [...new Set(cohorts.map((c) => c.platform))];
  if (cohorts.length === 0) return <p className="mkt-empty__range" data-testid="cohort-empty">No cohorts in this range yet</p>;
  return (
    <div className="mkt-cohorts" data-testid="cohort-grid">
      {platforms.map((p) => (
        <div key={p} className="overflow-x-auto mkt-cohort-wrap">
          <table role="table" className="mkt-cohort" data-testid={`cohort-grid-${p}`}>
            <caption className="mkt-dt__caption">{`Returning users, ${platformLabel(p)}`}</caption>
            <thead>
              <tr>
                <th scope="col">First-open week</th>
                <th scope="col" className="mkt-num">Users</th>
                {DAYS.map(([, label]) => <th key={label} scope="col" className="mkt-num">{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {cohorts.filter((c) => c.platform === p).map((c) => (
                <tr key={c.cohortWeek}>
                  <th scope="row">{`Week of ${formatDay(c.cohortWeek)}`}</th>
                  <td className="mkt-num">{c.size == null ? SUPPRESSED_TEXT : formatInt(c.size)}</td>
                  {DAYS.map(([k, label]) => {
                    const v = c[k];
                    const text = cohortCellText(c, v, k, today);
                    return (
                      <td key={k} className={`mkt-num mkt-cohort__cell mkt-cohort__cell--${cohortLevel(v)}`}
                        data-testid={`cohort-${p}-${c.cohortWeek}-${k}`}
                        title={text === SUPPRESSED_TEXT ? 'Fewer than 5 users: hidden' : text === DASH ? `${label} not reached yet` : undefined}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
